import axios, { AxiosResponse } from "axios";
import {CustomerSchema} from "@/models/customer_schema";
import { ActivitySchema } from "@/models/activity_schema";
import { SalesRegions } from "@/models/map/region_type";
import { AuthError, APIError } from "@/services/error_service";
import customer from "@/store/modules/customer";
/**
 * Creates a new Customer Service

 * @example
 * const customerService = new CustomerService();
 * customerService.getCustomers();
 */

 axios.defaults.withCredentials = true;
 axios.defaults.headers!.apikey = process.env.VUE_APP_API_KEY;

export default class CustomerService {
  /**
   * @returns {Customer[]} - response array of customer data objects
   */
  public async getCustomers(
    region?: SalesRegions["string"]
  ): Promise<CustomerSchema[]> {
    let customers;
    let url = process.env.VUE_APP_API_URL + "/customers";
    if (region) {
      url += "?region=" + region;
    }
    try {
      const options = {
        withCredentials: true,
      };
      const res: AxiosResponse<any> = await axios.get(url, options);
      customers = res.data.data.filter(c => !c.deleted_at)
    } catch (err) {
      if (err.response.status && err.response.status != 500) {
        if (err.response.status === 401) {
          throw new AuthError(err.response);
        } else if (err.response.status === 404) {
          throw new APIError("The requested information was not found.");
        } else if (err.response.status === 403) {
          throw new APIError(
            "You do not have access to the requested resource."
          );
        }
      } else {
        throw new Error("Something went wrong. Please try again.");
      }
    }
    return customers;
  }

  /**
   * POST new customer
   * @param {Customer} request - new customer data object
   * @returns {Customer} - newly created customer object
   */
  public async createNewCustomer(
    request: CustomerSchema
  ): Promise<CustomerSchema> {
    let customer;
    const options = {
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    };
    const body = JSON.stringify(request);
    try {
      const res: AxiosResponse<any> = await axios.post(
        process.env.VUE_APP_API_URL + "/customers",
        body,
        options
      );
      customer = res.data.data;
    } catch (err) {
      if (err.response.status && err.response.status != 500) {
        if (err.response.status === 401) {
          throw new AuthError(err.response);
        } else if (err.response.status === 404) {
          throw new APIError("The requested information was not found.");
        } else if (err.response.status === 403) {
          throw new APIError(
            "You do not have access to the requested resource."
          );
        } else if (err.response.status === 400) {
          throw new APIError("Invalid request. Please try again.");
        }
      } else {  
        throw new Error("Something went wrong. Please try again.");
      }
    }
    return customer;
  }

  /**
   * Get selected single customer
   * @param {number} id - customer id
   * @returns {Customer} - selected customer data object
   */
  public async getSingleCustomer(id: string): Promise<CustomerSchema> {
    let customer;
    const options = {
      withCredentials: true,
    };
    try {
      const res: AxiosResponse<any> = await axios.get(
        process.env.VUE_APP_API_URL + "/customers/" + id,
        options
      );
      customer = res.data.data;
    } catch (err) {
      if (err.response.status && err.response.status != 500) {
        if (err.response.status === 401) {
          throw new AuthError(err.response);
        } else if (err.response.status === 404) {
          throw new APIError("The requested information was not found.");
        } else if (err.response.status === 403) {
          throw new APIError(
            "You do not have access to the requested resource.", 403
          );
        }
      } else {
        throw new Error("Something went wrong. Please try again.");
      }
    }
    return customer;
  }

  /**
   * Update current customer
   * @param {number} id - customer id
   * @param {Customer} request - customer request data
   * @returns {Promise<void>}
   */
  public async updateCustomer(
    request: CustomerSchema,
    id: string
  ): Promise<void> {
    const options = {
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    };
    const body = JSON.stringify(request);
    try {
      return await axios.put(
        process.env.VUE_APP_API_URL + "/customers/" + id,
        body,
        options
      );
    } catch (err) {
      if (err.response.status && err.response.status != 500) {
        if (err.response.status === 401) {
          throw new AuthError(err.response);
        } else if (err.response.status === 404) {
          throw new APIError("The requested information was not found.");
        } else if (err.response.status === 403) {
          throw new APIError(
            "You do not have access to the requested resource."
          );
        } else if (err.response.status === 400) {
          throw new APIError("Invalid request. Please try again.");
        } else if (err.response.status === 409) {
          throw new APIError(
            "The salesperson entered already exists on another customer."
          );
        }
      } else {
        throw new Error("Something went wrong. Please try again.");
      }
    }
  }

  /**
   * Delete customer
   * @param {number} id - customer id
   * @returns {Promise<void>}
   */
  public async deleteCustomer(id: string): Promise<void> {
    try {
      const options = {
        withCredentials: true,
      };
      return await axios.delete(
        process.env.VUE_APP_API_URL + "/customers/" + id,
        options
      );
    } catch (err) {
      if (err.response.status && err.response.status != 500) {
        if (err.response.status === 401) {
          throw new AuthError(err.response);
        } else if (err.response.status === 404) {
          throw new APIError("The requested information was not found.");
        } else if (err.response.status === 403) {
          throw new APIError(
            "You do not have access to the requested resource."
          );
        }
      } else {
        throw new Error("Something went wrong. Please try again.");
      }
    }
  }

  /**
   * Return customer activity notes
   * @param {number} id - customer id
   * @returns {Promise<ActivitySchema>} - array of activity notes
   */
  public async getCustomerActivities(id: string): Promise<ActivitySchema[]> {
    let activities;
    const options = {
      withCredentials: true,
    };
    try {
      const res: AxiosResponse<any> = await axios.get(
        process.env.VUE_APP_API_URL + "/customers/" + id + "/activities",
        options
      );
      activities = res.data.data;
    } catch (err) {
      if (err.response.status && err.response.status != 500) {
        if (err.response.status === 401) {
          throw new AuthError(err.response);
        } else if (err.response.status === 404) {
          throw new APIError("The requested information was not found.");
        } else if (err.response.status === 403) {
          throw new APIError(
            "You do not have access to the requested resource."
          );
        }
      } else {
        throw new Error("Something went wrong. Please try again.");
      }
    }
    return activities;
  }
  /**
   * Post activity note on specified customer
   * @param {request} ActivitySchema
   * @param {number} id - customer id
   * @returns {Promise<ActivitySchema>} - returns object of new activity data
   */
  public async createNewActivity(
    request: ActivitySchema,
    id: string
  ): Promise<ActivitySchema> {
    let activity;
    const options = {
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    };
    const body = JSON.stringify(request);
    try {
      const res: AxiosResponse<any> = await axios.post(
        process.env.VUE_APP_API_URL + "/customers/" + id + "/activities",
        body,
        options
      );
      activity = res.data.data;
    } catch (err) {
      if (err.response.status && err.response.status != 500) {
        if (err.response.status === 401) {
          throw new AuthError(err.response);
        } else if (err.response.status === 404) {
          throw new APIError("The requested information was not found.");
        } else if (err.response.status === 403) {
          throw new APIError(
            "You do not have access to the requested resource."
          );
        } else if (err.response.status === 400) {
          throw new APIError("Invalid request. Please try again.");
        }
      } else {
        throw new Error("Something went wrong. Please try again.");
      }
    }
    return activity;
  }
}
